import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnInit,
} from '@angular/core';
import {
  ControlsOf,
  FormArray,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, distinctUntilChanged, Observable, tap } from 'rxjs';
import {
  Option,
  QuestionStructureOptions,
} from '../../core/models/question/question-options.model';
import { QuestionType } from '../../core/models/question/question-type.model';
import { Question } from '../../core/models/question/question.model';

import {
  FormQuestion,
  FormQuestionFollowUp,
  FormQuestionRepeater,
  FormQuestionStandard,
  FormRepeatable,
} from '../../core/models/form-group-mapping.model';
import { QuestionStructureRepeater } from '../../core/models/question/question-repeater.model';
import { RendererQuestionsRepository } from '../../core/repositories/renderer-questions.repository';
import { QuestionValueChangeService } from '../../core/services/question-value-change.service';
import { RepeatableMinLength } from './repeater/validators/repeatable-min-length.validator';

enum QuestionState {
  Visible = 'visible',
  Hidden = 'hidden',
}

@UntilDestroy()
@Component({
  selector: 'lib-question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionComponent implements OnInit, AfterViewChecked {
  _questionFormGroup: FormGroup<ControlsOf<FormQuestion>>;
  @Input() set questionFormGroup(value: FormGroup<ControlsOf<FormQuestion>>) {
    this._questionFormGroup = value;

    const childQuestions = [];
    if ((value.getRawValue() as FormQuestionFollowUp).followUpQuestions) {
      for (const childQuestion of (
        value.get('followUpQuestions') as FormArray<FormQuestion>
      ).controls) {
        childQuestions[childQuestion.value.id] = {
          childQuestionFormGroup: childQuestion,
          showChildQuestion: false,
        };
      }
    }
    this.childQuestionsSubject.next(childQuestions);
  }
  get questionFormGroup(): FormGroup<ControlsOf<FormQuestion>> {
    return this._questionFormGroup;
  }

  @Input() followUpLevel: number;
  @Input() labelPrefix = '';
  @Input() showAllFollowUps: boolean;
  @Input() repeaterGroupId: string;

  removeRepeaters = [];

  private _questionsInRow: number;
  @Input() set questionsInRow(value: number) {
    this._questionsInRow = value;
    const question = this.rendererQuestionsRepository.get(this.questionId);
    this.setUseMaxHeigthOnRow(question, value);
  }
  get questionsInRow() {
    return this._questionsInRow;
  }
  get questionId() {
    return this.questionFormGroup.getRawValue().id;
  }

  private _showQuestion: boolean;
  @Input() set showQuestion(value: boolean) {
    this._showQuestion = value;

    if (value && this.state === QuestionState.Hidden) {
      this.state = QuestionState.Visible;
      this.questionFormGroup.enable();
    } else if (!value && this.state === QuestionState.Visible) {
      this.state = QuestionState.Hidden;
      this.questionFormGroup.disable();
      this.clearQuestion();
    }
  }
  get showQuestion() {
    return this._showQuestion;
  }

  state: QuestionState = QuestionState.Visible;
  question$: Observable<Question>;
  parentQuestion: Question;

  childQuestionsSubject = new BehaviorSubject<
    {
      childQuestionFormGroup: FormGroup<ControlsOf<FormQuestion>>;
      showChildQuestion: boolean;
    }[]
  >([]);
  childQuestions$ = this.childQuestionsSubject.asObservable();
  useMaxHeigthOnRowSubject = new BehaviorSubject<boolean>(false);
  useMaxHeightOnRow$ = this.useMaxHeigthOnRowSubject.asObservable();

  QuestionState = QuestionState;

  constructor(
    private rendererQuestionsRepository: RendererQuestionsRepository,
    private questionValueChangeService: QuestionValueChangeService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.question$ = this.rendererQuestionsRepository
      .select(this.questionId)
      .pipe(
        tap((question) => {
          this.setUseMaxHeigthOnRow(question, this.questionsInRow);
        }),
      );

    if (
      (this.questionFormGroup.value as FormQuestionFollowUp).followUpQuestions
    ) {
      const question = this.rendererQuestionsRepository.get(this.questionId);
      (this.questionFormGroup.get('value') as FormControl<FormQuestion>).value$
        .pipe(distinctUntilChanged(), untilDestroyed(this))
        .subscribe((value) => {
          this.setShowChildQuestions(value, question);
        });

      this.questionValueChangeService
        .patchValueListener(
          question,
          this.repeaterGroupId,
          this.questionFormGroup.get('value') as FormControl<string>,
        )
        .pipe(untilDestroyed(this))
        .subscribe((answer) => {
          const answerValue = this.getAnswerValue(answer.value, question.type);
          this.setShowChildQuestions(answerValue, question);
        });
    }
  }

  ngAfterViewChecked() {
    this.cdr.markForCheck();
  }

  trackByFn(index) {
    return index;
  }

  private getValues(question: Question, value) {
    if (question.type === QuestionType.Checkbox) {
      return value ? JSON.parse(value) : [];
    } else {
      return value ? [value] : [];
    }
  }

  private normalizeToArray(values: string | string[]): string[] {
    const rawValues = Array.isArray(values) ? values : [values];

    return rawValues.flatMap((value) => {
      if (typeof value !== 'string' || value.length === 0) {
        return [];
      }
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    });
  }

  private getAnswerValue(value: string, questionType: QuestionType) {
    if (!value) {
      return;
    }
    if (questionType === QuestionType.Checkbox) {
      return value;
    } else {
      const parsed = JSON.parse(value) as string[];
      return parsed.length > 0 ? parsed[0] : undefined;
    }
  }

  public setUseMaxHeigthOnRow(question: Question, questionsInRow: number) {
    const numberOfFollowupQuestions = (
      question?.structure as QuestionStructureOptions
    )?.followUpQuestionsOrder
      ? (question.structure as QuestionStructureOptions).followUpQuestionsOrder
          .length
      : 0;
    this.useMaxHeigthOnRowSubject.next(
      questionsInRow > 1 && numberOfFollowupQuestions === 0,
    );
  }

  private setShowChildQuestions(value, question: Question) {
    let values = this.getValues(question, value);
    values = this.normalizeToArray(values);
    const options = (question.structure as QuestionStructureOptions).options;

    const structure = question.structure as QuestionStructureOptions;
    let selectedOptions: Option[] = [];
    if (structure.organisationOption) {
      const allFollowUpIds = structure.followUpQuestionsOrder?.flat() ?? [];
      if (values.length > 0) {
        selectedOptions = [
          {
            value: '',
            text: '',
            followUpQuestionsIds: allFollowUpIds,
          },
        ];
      }
    } else {
      selectedOptions = options.filter((option) =>
        values.includes(option.value),
      );
    }

    const childQuestions = this.childQuestionsSubject.value;
    for (const [childQuestionId, childQuestion] of Object.entries(
      childQuestions,
    )) {
      const showChildQuestion = selectedOptions.some(
        (selectedOption) =>
          selectedOption.followUpQuestionsIds &&
          selectedOption.followUpQuestionsIds.includes(childQuestionId),
      );

      if (!showChildQuestion && childQuestion.showChildQuestion) {
        this.removeRepeaters.push(childQuestionId);
      }

      childQuestion.showChildQuestion = showChildQuestion;

      if (
        childQuestion.showChildQuestion &&
        (childQuestion.childQuestionFormGroup.value as FormQuestionRepeater)
          .repeatables &&
        this.removeRepeaters.includes(childQuestionId)
      ) {
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.clearRepeatables(
              childQuestion.childQuestionFormGroup,
              childQuestionId,
              false,
            );
            this.cdr.detectChanges();
          });
        });
        this.removeRepeaters = this.removeRepeaters.filter(
          (r) => r !== childQuestionId,
        );
      }
    }

    this.childQuestionsSubject.next(childQuestions);
  }

  private clearQuestion() {
    if ((this.questionFormGroup.value as FormQuestionRepeater).repeatables) {
      this.clearRepeatables(this.questionFormGroup, this.questionId, true);
    } else {
      this.clearQuestionAndFollowUps();
    }
  }

  private clearRepeatables(
    formGroup: FormGroup<ControlsOf<FormQuestion>>,
    questionId: string,
    removeGroupsBackend: boolean,
  ) {
    const disabled = formGroup.disabled;

    if (removeGroupsBackend) {
      const repeatables = (formGroup.value as FormQuestionRepeater).repeatables;
      for (const repeatable of repeatables) {
        this.questionValueChangeService.removeRepeaterGroup(repeatable.id);
      }
    }

    formGroup.removeControl('repeatables');
    const repeatableFormControl = new FormArray<FormRepeatable>([]);

    const question = this.rendererQuestionsRepository.get(questionId);
    if (
      (question.structure as QuestionStructureRepeater).minNumberOfRepeatables
    ) {
      repeatableFormControl.addValidators(
        RepeatableMinLength(
          (question.structure as QuestionStructureRepeater)
            .minNumberOfRepeatables,
        ),
      );
    }
    formGroup.setControl('repeatables', repeatableFormControl);
    if (disabled) {
      formGroup.disable();
    }
  }

  private clearQuestionAndFollowUps() {
    (this.questionFormGroup as FormGroup<ControlsOf<FormQuestionStandard>>)
      .get('value')
      .reset(undefined, { emitEvent: false });
    if (
      (this.questionFormGroup.value as FormQuestionFollowUp).followUpQuestions
    ) {
      const followUpQuestionsFormArray = this.questionFormGroup.get(
        'followUpQuestions',
      ) as FormArray<FormQuestionStandard>;
      for (const followUpQuestionFormGroup of followUpQuestionsFormArray.controls) {
        followUpQuestionFormGroup
          .get('value')
          .reset(undefined, { emitEvent: false });
      }
    }
  }
}
